import { useRef } from 'react';
import {
  DatePicker,
  DatePickerInput,
  RadioButton,
  RadioButtonGroup,
  Select,
  SelectItem,
} from '@carbon/react';
import { useStore } from '@tanstack/react-form';
import { TextAreaField, TextField } from 'components/form-fields-2';
import { fromDateParam, toDateParam } from 'utils/dates';
import {
  DISCOUNT_TO_DATE_TYPE_OPTIONS,
  EVALUATION_TYPE_OPTIONS,
  type validateCase,
} from './utils';
import type { CaseFormInstance } from './hooks';
import type { DiscountToDateType, EvaluationType } from './types';
import styles from './case-editor.module.scss';

interface CaseDetailsFieldsProps {
  form: CaseFormInstance;
  /** A save was attempted: show the required-field messages. */
  attempted: boolean;
  errors: ReturnType<typeof validateCase>;
}

/**
 * The case's own fields: name and evaluation type (required), the discount-to-date type and its
 * date, and notes.
 *
 * @param {CaseDetailsFieldsProps} props - The form, whether a save was attempted, and its errors.
 * @return {JSX.Element} The fields.
 */
export function CaseDetailsFields({
  form,
  attempted,
  errors,
}: CaseDetailsFieldsProps) {
  const discountToDateType = useStore(
    form.store,
    (state) => state.values.discountToDateType
  );
  const discountToDate = useStore(
    form.store,
    (state) => state.values.discountToDate
  );
  // The last fixed date picked, restored when the type goes Floating and back to Fixed.
  const lastFixedDate = useRef(form.state.values.discountToDate);

  return (
    <section className={styles.details} aria-label="Case details">
      <form.Field name="name">
        {(field) => (
          <TextField
            id="case-name"
            labelText="Case Name *"
            placeholder="e.g. 2026 Permian Acquisition — Base Case"
            value={field.state.value}
            onChange={(value) => field.handleChange(value)}
            onBlur={field.handleBlur}
            invalid={attempted && Boolean(errors.name)}
            invalidText={errors.name}
          />
        )}
      </form.Field>

      {/* A native Select, not a Carbon Dropdown: the Dropdown's controlled selection re-fires
          its change through the form store and loops in a real browser. */}
      <form.Field name="evaluationType">
        {(field) => (
          <Select
            id="case-evaluation-type"
            labelText="Evaluation Type *"
            value={field.state.value}
            onChange={(event) =>
              field.handleChange(event.target.value as EvaluationType | '')
            }
            invalid={attempted && Boolean(errors.evaluationType)}
            invalidText={errors.evaluationType}
          >
            <SelectItem value="" text="Select type..." />
            {EVALUATION_TYPE_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                text={option.label}
              />
            ))}
          </Select>
        )}
      </form.Field>

      <div className={styles.discountToDate}>
        <RadioButtonGroup
          legendText="Discount To Date"
          name="case-discount-to-date-type"
          valueSelected={discountToDateType}
          onChange={(value) => {
            form.setFieldValue(
              'discountToDateType',
              value as DiscountToDateType
            );
            form.setFieldValue(
              'discountToDate',
              value === 'fixed' ? lastFixedDate.current : null
            );
          }}
        >
          {DISCOUNT_TO_DATE_TYPE_OPTIONS.map((option) => (
            <RadioButton
              key={option.value}
              id={`case-discount-date-${option.value}`}
              value={option.value}
              labelText={option.label}
            />
          ))}
        </RadioButtonGroup>
        {discountToDateType === 'fixed' && (
          <DatePicker
            datePickerType="single"
            // A stable mm/dd/yyyy string: the picker parses a string only in that form.
            value={fromDateParam(discountToDate)}
            // On the DatePicker: it hands its own `invalid` to the input.
            invalid={attempted && Boolean(errors.discountToDate)}
            onChange={([date]: Date[]) => {
              if (!date) return;
              lastFixedDate.current = toDateParam(date);
              form.setFieldValue('discountToDate', lastFixedDate.current);
            }}
          >
            <DatePickerInput
              id="case-discount-to-date"
              labelText="Date"
              placeholder="mm/dd/yyyy"
              invalidText={errors.discountToDate}
            />
          </DatePicker>
        )}
      </div>

      <form.Field name="notes">
        {(field) => (
          <TextAreaField
            id="case-notes"
            labelText="Notes"
            placeholder="Brief description, context, or caveats for this Case."
            value={field.state.value}
            onChange={(value) => field.handleChange(value)}
            onBlur={field.handleBlur}
            rows={3}
          />
        )}
      </form.Field>
    </section>
  );
}
